import type { ChartConfig } from '@/shared/components/ui/chart'
import type { DriverRow } from '../lib/demand-drivers'

import { format, parseISO } from 'date-fns'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { formatDate } from '../lib/period'

const CHART_COLORS = 5

const formatTick = (date: string) => format(parseISO(date), 'd MMM')

const formatTooltipDate = (value: unknown) =>
	typeof value === 'string' ? formatDate(value) : ''

const formatPercent = (value: number) =>
	formatNumber(value * 100, NumberFormats.Percent)
const colors = ['#C1C3C5', '#92DDB7', '#FF9800', '#32B2F3', '#3F51B5']
export const DemandDriversChart = ({
	rows,
	drivers,
	labels,
	height = 180,
	'data-testid': testId,
}: {
	rows: DriverRow[]
	drivers: string[]
	labels: Record<string, string>
	height?: number
	'data-testid'?: string
}) => {
	const config = Object.fromEntries(
		drivers.map((name, i) => [
			name,
			{
				label: labels[name] ?? name,
				color: colors[i % CHART_COLORS],
			},
		])
	) satisfies ChartConfig

	return (
		<ChartContainer
			config={config}
			className='aspect-auto w-full'
			style={{ height }}
			data-testid={testId}
		>
			<BarChart
				accessibilityLayer={false}
				data={rows}
				margin={{ top: 8, right: 4, bottom: 0, left: 0 }}
				barCategoryGap='20%'
				stackOffset='none'
			>
				<CartesianGrid vertical={false} strokeDasharray='3 3' />
				<XAxis
					dataKey='date'
					tickFormatter={formatTick}
					minTickGap={32}
					tickLine={false}
					axisLine={false}
					fontSize={10}
				/>
				<YAxis
					domain={[0, 1]}
					ticks={[0, 0.25, 0.5, 0.75, 1]}
					tickFormatter={formatPercent}
					width={34}
					tickLine={false}
					axisLine={false}
					fontSize={10}
				/>
				<ChartTooltip
					cursor={{ fill: 'var(--muted)', fillOpacity: 0.4 }}
					content={
						<ChartTooltipContent
							labelFormatter={formatTooltipDate}
							formatter={(value, name, item) => (
								<div className='flex w-full items-center justify-between gap-2 leading-none'>
									<span className='flex items-center gap-1.5 text-background/50'>
										<span
											className='size-2.5 shrink-0 rounded-[2px]'
											style={{ background: item.color ?? item.payload?.fill }}
										/>
										{labels[String(name)] ?? String(name)}
									</span>
									<span className='font-mono font-medium tabular-nums'>
										{formatPercent(Number(value))}
									</span>
								</div>
							)}
						/>
					}
				/>
				<ChartLegend content={<ChartLegendContent nameKey='value' />} />
				{drivers.map((name) => (
					<Bar
						key={name}
						dataKey={`shares.${name}`}
						name={name}
						stackId='drivers'
						fill={`var(--color-${name})`}
						isAnimationActive={false}
					/>
				))}
			</BarChart>
		</ChartContainer>
	)
}
